

import { useEffect, useState } from "react";
import { toast } from "../../../lib/toast";
import * as meetingService from "../../../services/meeting.service";
import * as projectService from "../../../services/project.service";
import * as topicoService from "../../../services/topico.service";
import * as itemAtaService from "../../../services/itemAta.service";
import * as participanteService from "../../../services/participante.service";
import * as desenvolvimentoService from "../../../services/desenvolvimento.service";
import type { Meeting } from "../../../services/meeting.service";
import type { Project } from "../../../services/project.service";
import type { Topico } from "../../../services/topico.service";
import type { ItemAta } from "../../../services/itemAta.service";
import type { Participante } from "../../../services/participante.service";
import type { Desenvolvimento } from "../../../services/desenvolvimento.service";

interface UsePreviewDataParams {
  projectId: string;
  meetingId: string;
}

interface OrganizedData {
  topicos: Array<Topico & { items: Array<ItemAta & { desenvolvimentos: Desenvolvimento[] }> }>;
  itemsSemTopico: Array<ItemAta & { desenvolvimentos: Desenvolvimento[] }>;
}

interface UsePreviewDataReturn {
  meeting: Meeting | null;
  project: Project | null;
  topicos: Topico[];
  participantes: Participante[];
  organizedData: OrganizedData;
  loading: boolean;
  error: string | null;
  lastMeetingNumber: number;
}

export function usePreviewData({
  projectId,
  meetingId,
}: UsePreviewDataParams): UsePreviewDataReturn {
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [topicos, setTopicos] = useState<Topico[]>([]);
  const [items, setItems] = useState<ItemAta[]>([]);
  const [participantes, setParticipantes] = useState<Participante[]>([]);
  const [desenvolvimentos, setDesenvolvimentos] = useState<Desenvolvimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastMeetingNumber, setLastMeetingNumber] = useState<number>(0);

  useEffect(() => {
    if (!projectId || !meetingId) return;

    const fetchAll = async () => {
      try {
        setLoading(true);
        setError(null);


        const [
          meetingData,
          projectData,
          todosTopicosAteAta,
          topicosAtaAtual,
          itemsData,
          participantesData,
          projectMeetingsData,
        ] = await Promise.all([
          meetingService.getMeetingById(meetingId),
          projectService.getProjectById(projectId),
          topicoService.getTopicosByProjetoAteAta(projectId, meetingId).catch(() => []),
          topicoService.getTopicosByAtaId(meetingId).catch(() => []),
          itemAtaService.getItemsByAtaId(meetingId),
          participanteService.getParticipantesByAtaId(meetingId).catch(() => []),
          meetingService.getProjectMeetings(projectId),
        ]);

        // Extrair array de meetings da resposta (nova estrutura retorna { meetings, permission })
        const allProjectMeetings = projectMeetingsData.meetings;

        setMeeting(meetingData);
        setProject(projectData);
        
        // Criar mapa de tópicos da ata atual por codigo_mapa para fácil lookup
        const topicosAtuaisPorCodigo = new Map<string, typeof topicosAtaAtual[0]>();
        for (const topico of topicosAtaAtual) {
          const topicoComCodigo = topico as any;
          const codigo = topicoComCodigo.codigo_mapa || String(Number(topico.ordem) + 1);
          topicosAtuaisPorCodigo.set(codigo, topico);
        }
        
        // Usar tópicos da ata atual quando existirem (para pegar ID correto), senão usar do histórico
        const topicosFinais = todosTopicosAteAta.map(topicoHistorico => {
          const topicoComCodigo = topicoHistorico as any;
          const codigo = topicoComCodigo.codigo_mapa || String(Number(topicoHistorico.ordem) + 1);
          return topicosAtuaisPorCodigo.get(codigo) || topicoHistorico;
        });
        
        setTopicos(topicosFinais);
        setItems(itemsData);
        setParticipantes(participantesData);


        const maxNumber = allProjectMeetings.reduce(
          (max, m) => Math.max(max, m.number || 0),
          0
        );
        setLastMeetingNumber(maxNumber);


        const desenvolvimentosPorItem = await desenvolvimentoService
          .getDesenvolvimentosByAtaId(meetingId)
          .catch(() => ({}));
        setDesenvolvimentos(Object.values(desenvolvimentosPorItem).flat());
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Erro ao carregar ata";
        setError(message);
        toast.error(message);
        console.error("Erro ao carregar dados da ata:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [projectId, meetingId]);


  const organizedData: OrganizedData = {
    topicos: topicos.map((topico) => ({
      ...topico,
      items: items
        .filter((item) => item.topico_id === topico.id)
        .map((item) => ({
          ...item,
          desenvolvimentos: desenvolvimentos.filter(
            (dev) => dev.item_id === item.id
          ),
        })),
    })),
    itemsSemTopico: items
      .filter((item) => !item.topico_id)
      .map((item) => ({
        ...item,
        desenvolvimentos: desenvolvimentos.filter(
          (dev) => dev.item_id === item.id
        ),
      })),
  };

  return {
    meeting,
    project,
    topicos,
    participantes,
    organizedData,
    loading,
    error,
    lastMeetingNumber,
  };
}

